/* global window, document */

import * as React from 'react';
import debounce from 'lodash.debounce';

import * as PropTypes from 'prop-types';

import { UitkCard, UitkCardContent } from 'uitk-react-cards';

import { Animation, AnimationType, TIME } from 'components/common/animation';

export interface OverlayState {
  overlayHeight: number | string;
}

export interface OverlayProps {
  children: JSX.Element;
  closeCallback?: () => void;
  openAnimation?: string;
  openCallback?: () => void;
  type: 'bottomSheet' | 'custom' | 'modal' | 'takeover';
}

export class Overlay extends React.Component<OverlayProps, OverlayState> {
  private debouncedResizeFn;
  private GSAPosition;
  private openTimeout;

  constructor(props) {
    super(props);

    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // set overlay height for Google Search App (GSA) only
      const appVersion = window.navigator && window.navigator.appVersion;

      if (appVersion) {
        this.GSAPosition = appVersion.search('GSA');

        if (this.GSAPosition > 0) {
          this.state = {
            overlayHeight: Math.min(document.documentElement.clientHeight, window.innerHeight)
          };
        } else {
          this.state = {
            overlayHeight: ''
          };
        }
      }
    }

    this.handleResize = this.handleResize.bind(this);
  }

  public static propTypes = {
    children: PropTypes.element.isRequired,
    closeCallback: PropTypes.func,
    openCallback: PropTypes.func,
    type: PropTypes.oneOf(['modal', 'takeover', 'bottomSheet', 'custom']).isRequired
  };

  public componentDidMount() {
    if (this.props.openCallback) {
      if (this.openTimeout) {
        clearTimeout(this.openTimeout);
      }

      this.openTimeout = setTimeout(() => {
        this.props.openCallback();
      }, TIME);
    }

    if (typeof window !== 'undefined') {
      if (this.GSAPosition > 0) {
        this.debouncedResizeFn = debounce(this.handleResize, 200);
        window.addEventListener('resize', this.debouncedResizeFn);
      }
    }
  }

  public componentWillUnmount() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
    }

    if (this.props.closeCallback) {
      this.props.closeCallback();
    }

    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.debouncedResizeFn);
    }
  }

  private buildOverlayContent(content, openAnimation) {
    if (openAnimation) {
      return (
        <Animation addChildAnimation={AnimationType.NONE} mountAnimation={openAnimation} removeChildAnimation={AnimationType.NONE}>
          {content}
        </Animation>
      );
    }

    return content;
  }

  private handleResize() {
    this.setState({ overlayHeight: Math.min(document.documentElement.clientHeight, window.innerHeight) });
  }

  private overlayFactory(type) {
    const { children, openAnimation } = this.props;

    switch (type) {
      case 'modal':
        return this.buildOverlayContent(
          <UitkCard className="overlay-modal overlay-content overlay-modal-inherit">
            <UitkCardContent>{children}</UitkCardContent>
          </UitkCard>,
          openAnimation
        );

      case 'takeover':
        return this.buildOverlayContent(
          <div className="overlay-takeover overlay-content" style={{ height: this.state.overlayHeight }}>
            {children}
          </div>,
          openAnimation
        );

      case 'bottomSheet':
        return this.buildOverlayContent(<div className="overlay-bottom-sheet overlay-content">{children}</div>, openAnimation);

      case 'custom':
        return this.buildOverlayContent(<div className="overlay-custom overlay-content">{children}</div>, openAnimation);

      default:
        // should hopefully never get here with propType checks, but better to be safe and identify the problem
        throw new Error(`Overlay Error: attempted to launch an invalid overlay of type '${type}'`);
    }
  }

  public render() {
    return this.overlayFactory(this.props.type);
  }
}
